import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useSchoolBrand } from '../../hooks/useSchoolBrand';
import { useSchoolModules } from '../../hooks/useSchoolModules';
import {
  HomeIcon, BanknotesIcon, CurrencyDollarIcon, CubeIcon,
  DocumentTextIcon, InboxStackIcon, UserCircleIcon,
  XMarkIcon, ChartBarIcon, CalendarDaysIcon, ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon, FingerPrintIcon, CreditCardIcon, BuildingOfficeIcon,
  MegaphoneIcon, BookOpenIcon, HomeModernIcon,
} from '@heroicons/react/24/outline';

const BASE_NAV = [
  { name: 'Dashboard',        href: '/finance/dashboard',        icon: HomeIcon },
  { name: 'Attendance',       href: '/finance/attendance',       icon: CalendarDaysIcon },
  { name: 'Leaves',           href: '/finance/leaves',           icon: ClipboardDocumentListIcon },
  { name: 'Staff Attendance', href: '/finance/staff-attendance', icon: ClipboardDocumentCheckIcon },
  { name: 'Staff Leaves',     href: '/finance/staff-leaves',     icon: ClipboardDocumentListIcon },
  { name: 'Staff Management (Non-Portal)', href: '/finance/staff-management', icon: BuildingOfficeIcon },
  { name: 'Fees',             href: '/finance/fees',             icon: BanknotesIcon },
  { name: 'Staff Salary',     href: '/finance/salary',           icon: ChartBarIcon },
  { name: 'My Salary',        href: '/finance/my-salary',        icon: BanknotesIcon },
  { name: 'Expenses',         href: '/finance/expenses',         icon: CurrencyDollarIcon },
  { name: 'Assets',           href: '/finance/assets',           icon: CubeIcon },
  { name: 'Biometric & RFID', href: '/finance/biometric',        icon: FingerPrintIcon },
  { name: 'Student Gate Log', href: '/finance/student-gate-log', icon: CreditCardIcon },
  { name: 'Certificates',     href: '/finance/certificates',     icon: DocumentTextIcon },
  { name: 'Notices',          href: '/finance/notices',          icon: MegaphoneIcon, badge: 'notices' },
  { name: 'My Requests',      href: '/finance/requests',         icon: InboxStackIcon, badge: 'requests' },
  { name: 'Profile',          href: '/finance/profile',          icon: UserCircleIcon },
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .fs-wrap { width:240px; min-width:240px; min-height:100vh; background:#0d1f1a; display:flex; flex-direction:column; overflow-x:hidden; font-family:'Sora',sans-serif; border-right:1px solid rgba(255,255,255,.04); }
  .fs-brand { padding:20px 20px 16px; border-bottom:1px solid rgba(255,255,255,.06); flex-shrink:0; position:relative; }
  .fs-brand-row { display:flex; align-items:center; gap:10px; }
  .fs-brand-icon { width:34px; height:34px; background:#0d9488; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .fs-brand-icon svg { width:18px; height:18px; stroke:white; stroke-width:2; }
  .fs-brand-name { font-size:.95rem; font-weight:700; color:#fff; letter-spacing:-.02em; }
  .fs-brand-sub  { font-size:.68rem; color:#2d6b62; margin-top:3px; letter-spacing:.04em; text-transform:uppercase; }
  .fs-nav { flex:1; padding:10px; overflow-y:auto; overflow-x:hidden; scrollbar-width:none; }
  .fs-nav::-webkit-scrollbar { display:none; }
  .fs-link { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:10px; text-decoration:none; color:#5a9e96; font-size:.81rem; font-weight:500; transition:background .15s,color .15s; margin-bottom:1px; position:relative; }
  .fs-link-text { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .fs-link:hover { background:rgba(255,255,255,.04); color:#a7f3d0; }
  .fs-link.active { background:rgba(13,148,136,.15); color:#0d9488; }
  .fs-link.active::before { content:''; position:absolute; left:-10px; top:25%; bottom:25%; width:3px; background:#0d9488; border-radius:0 3px 3px 0; }
  .fs-link svg { width:17px; height:17px; stroke-width:1.7; flex-shrink:0; }
  .fs-footer { padding:14px 12px; border-top:1px solid rgba(255,255,255,.05); }
  .fs-footer-text { font-size:.68rem; color:#2d6b62; text-align:center; }
  .fs-close { display:none; }
  .fs-badge { margin-left:auto; min-width:18px; height:18px; padding:0 5px; border-radius:99px; background:#0d9488; color:white; font-size:.6rem; font-weight:700; display:flex; align-items:center; justify-content:center; }
  @media (max-width:768px) {
    .fs-close { display:flex; align-items:center; justify-content:center; position:absolute; top:16px; right:16px; width:28px; height:28px; border-radius:8px; background:rgba(255,255,255,.08); border:none; cursor:pointer; }
    .fs-close svg { width:15px; height:15px; stroke:white; stroke-width:2; }
  }
`;

const FinanceSidebar = ({ onClose }) => {
  const location = useLocation();
  const { brand } = useSchoolBrand();
  const { libraryEnabled, hostelEnabled } = useSchoolModules();
  const [pendingCount, setPendingCount] = useState(0);
  const [noticeCount,  setNoticeCount]  = useState(0);

  const staffMgmtIdx = BASE_NAV.findIndex(n => n.name === 'Staff Management (Non-Portal)');
  const NAV = [
    ...BASE_NAV.slice(0, staffMgmtIdx + 1),
    ...(libraryEnabled ? [
      { name: 'Librarians', href: '/finance/librarians', icon: BookOpenIcon },
      { name: 'Library',    href: '/finance/library',    icon: BookOpenIcon },
    ] : []),
    ...(hostelEnabled ? [
      { name: 'Hostel Members', href: '/finance/hostel-members',  icon: HomeModernIcon },
      { name: 'Hostel',         href: '/finance/hostel-overview', icon: HomeModernIcon },
    ] : []),
    ...BASE_NAV.slice(staffMgmtIdx + 1),
  ];

  useEffect(() => {
    const lastSeen = Number(localStorage.getItem('finance_requests_seen') || 0);
    api.get('/finance/requests?status=approved').then(r => {
      const data   = r.data || [];
      const unseen = data.filter(req => {
        const resolved = new Date(req.resolvedAt || req.updatedAt).getTime();
        return resolved > lastSeen;
      });
      setPendingCount(unseen.length);
    }).catch(() => {});
    api.get('/finance/notices/unread-count').then(r => setNoticeCount(r.data?.count || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/finance/requests')) {
      localStorage.setItem('finance_requests_seen', Date.now().toString());
      setPendingCount(0);
    }
    if (location.pathname.startsWith('/finance/notices')) setNoticeCount(0);
  }, [location.pathname]);

  const getBadge = (item) => {
    if (!item.badge) return null;
    const count = item.badge === 'notices' ? noticeCount : item.badge === 'requests' ? pendingCount : 0;
    if (!count) return null;
    return <span className="fs-badge">{count > 99 ? '99+' : count}</span>;
  };

  return (
    <>
      <style>{css}</style>
      <div className="fs-wrap">
        <div className="fs-brand">
          <div className="fs-brand-row">
            {brand.logo
              ? <img src={brand.logo} alt="logo" style={{width:34,height:34,borderRadius:10,objectFit:'contain',background:'rgba(255,255,255,.95)',padding:2,border:'1px solid rgba(255,255,255,.12)',flexShrink:0}}/>
              : <div className="fs-brand-icon"><BanknotesIcon /></div>
            }
            <div>
              <div className="fs-brand-name">{brand.name || 'School Portal'}</div>
              <div className="fs-brand-sub">Finance Dashboard</div>
            </div>
          </div>
          {onClose && <button className="fs-close" onClick={onClose}><XMarkIcon /></button>}
        </div>
        <nav className="fs-nav">
          {NAV.map(item => {
            const active = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
            return (
              <Link key={item.name} to={item.href} className={`fs-link${active ? ' active' : ''}`} onClick={onClose}>
                <item.icon />
                <span className="fs-link-text">{item.name}</span>
                {getBadge(item)}
              </Link>
            );
          })}
        </nav>
        <div className="fs-footer"><div className="fs-footer-text">Finance Panel · v1.0</div></div>
      </div>
    </>
  );
};

export default FinanceSidebar;